import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, dateFmt, formatErr } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Check, CreditCard, ExternalLink, FileText, Receipt } from "lucide-react";

export default function Billing() {
    const { tenant } = useAuth();
    const [plans, setPlans] = useState([]);
    const [summary, setSummary] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [busy, setBusy] = useState("");

    useEffect(() => {
        api.get("/billing/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
        api.get("/billing/summary").then((r) => setSummary(r.data)).catch(() => {});
        api.get("/billing/invoices").then((r) => setInvoices(r.data.invoices || [])).catch(() => {});
    }, []);

    const subscribe = async (lookup_key) => {
        setBusy(lookup_key);
        try {
            const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
            window.location.href = data.checkout_url;
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); setBusy(""); }
    };

    const status = summary?.subscription_status || tenant?.subscription_status || "trialing";
    const isActive = status === "active";

    return (
        <div className="space-y-6 pe-fade-up" data-testid="billing-page">
            <div>
                <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-brand-emerald">Billing & Subscription</h1>
                <p className="text-muted-foreground text-sm mt-1">Manage your ProcureEd annual subscription.</p>
            </div>

            <div className="bg-emerald-50 border border-emerald-100 text-brand-emerald rounded-xl p-6 flex flex-wrap items-center justify-between gap-4">
                <div>
                    <div className="text-sm text-slate-600">Current status</div>
                    <div className="mt-2 flex items-center gap-3">
                        <StatusBadge status={status} testid="subscription-status" />
                        {summary?.trial_end && status === "trialing" && (
                            <span className="text-sm text-slate-600">Trial ends {dateFmt(summary.trial_end)}</span>
                        )}
                        {isActive && summary?.plan && <span className="text-sm text-slate-600 capitalize">{summary.plan} plan</span>}
                    </div>
                </div>
                <CreditCard className="h-8 w-8 text-emerald-300" />
            </div>

            {!isActive && (
                <div className="grid md:grid-cols-2 gap-5">
                    {plans.map((p) => (
                        <div key={p.lookup_key} className="bg-white border border-border rounded-xl p-6 flex flex-col" data-testid={`plan-${p.lookup_key}`}>
                            <h3 className="text-lg font-semibold text-brand-emerald">{p.name}</h3>
                            <p className="text-sm text-muted-foreground mt-1">{p.tagline}</p>
                            <div className="mt-4 flex items-baseline gap-1">
                                <span className="text-4xl font-semibold font-display text-brand-emerald">{money(p.price)}</span>
                                <span className="text-muted-foreground">/ year</span>
                            </div>
                            <ul className="mt-5 space-y-2 flex-1">
                                {p.features.map((f) => (
                                    <li key={f} className="flex items-start gap-2 text-sm"><Check className="h-4 w-4 text-brand-teal mt-0.5 shrink-0" /> {f}</li>
                                ))}
                            </ul>
                            <Button disabled={busy} onClick={() => subscribe(p.lookup_key)} className="mt-6 bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid={`subscribe-${p.lookup_key}`}>
                                {busy === p.lookup_key ? <Loader2 className="h-4 w-4 animate-spin" /> : "Subscribe"}
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            {isActive && (
                <div className="bg-white border border-border rounded-xl p-6 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-emerald-800"><Check className="h-5 w-5" /> Your subscription is active. Thank you for using ProcureEd.</div>
                </div>
            )}

            <div className="bg-white border border-border rounded-xl overflow-hidden" data-testid="receipts-card">
                <div className="px-5 py-4 border-b border-border font-semibold text-brand-emerald">Invoices & Receipts</div>
                {invoices.length === 0 ? (
                    <div className="px-5 py-8 text-sm text-muted-foreground text-center">No Stripe invoices yet. They'll appear here after your first payment.</div>
                ) : (
                    <div className="divide-y divide-border">
                        {invoices.map((inv, i) => (
                            <div key={i} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm" data-testid={`receipt-row-${i}`}>
                                <span className="flex items-center gap-3">
                                    <span className="font-mono text-brand-teal">{inv.number || "—"}</span>
                                    <span className="text-muted-foreground">{inv.created ? dateFmt(new Date(inv.created * 1000).toISOString()) : ""}</span>
                                </span>
                                <span className="flex items-center gap-3">
                                    <span className="font-mono">{money(inv.amount_paid)}</span>
                                    <StatusBadge status={inv.status === "paid" ? "active" : "trialing"} />
                                    {inv.hosted_invoice_url && (
                                        <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brand-teal hover:underline" data-testid={`receipt-view-${i}`}>
                                            <ExternalLink className="h-3.5 w-3.5" /> Invoice
                                        </a>
                                    )}
                                    {inv.receipt_url && (
                                        <a href={inv.receipt_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brand-emerald hover:underline" data-testid={`receipt-link-${i}`}>
                                            <Receipt className="h-3.5 w-3.5" /> Receipt
                                        </a>
                                    )}
                                    {inv.invoice_pdf && (
                                        <a href={inv.invoice_pdf} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-muted-foreground hover:text-brand-emerald" data-testid={`receipt-pdf-${i}`}>
                                            <FileText className="h-3.5 w-3.5" /> PDF
                                        </a>
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {summary?.transactions?.length > 0 && (
                <div className="bg-white border border-border rounded-xl overflow-hidden">
                    <div className="px-5 py-4 border-b border-border font-semibold text-brand-emerald">Payment History</div>
                    <div className="divide-y divide-border">
                        {summary.transactions.map((t) => (
                            <div key={t.session_id} className="flex items-center justify-between px-5 py-3 text-sm">
                                <span>{t.lookup_key} · {dateFmt(t.created_at)}</span>
                                <span className="flex items-center gap-3"><span className="font-mono">{money(t.amount)}</span><StatusBadge status={t.payment_status === "paid" ? "active" : "trialing"} /></span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <p className="text-xs text-muted-foreground">Test card: 4242 4242 4242 4242 · any future expiry · any CVC</p>
        </div>
    );
}
